import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { type Category, CategoryApi, CategoryCreate } from '@shared/interfacres/category';
import { inject } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';

import { CategoriesManagerService } from '@data-access/manager/services/categories-manager.service';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/basic';
import { MatSnackBar } from '@angular/material/snack-bar';


interface CategoriesState {
  items: Category[];
  fullList: Category[];
  total: number;
  name: string;
  isLoading: boolean;
  filter: GetFilter;
  pagination: GetPagination;
}

const initialState: CategoriesState = {
  items: [],
  fullList: [],
  total: 0,
  name: '',
  isLoading: false,
  filter: { query: '', order: 'asc' },
  pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
};

export const CategoriesManagerStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(CategoriesManagerService), snackBar = inject(MatSnackBar)) => ({


    getByName(name: string) {
      patchState(store, () => ({
        name
      }));
      return this.getFiltered();
    },

    setPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        pagination: {
          ...state.pagination,
          page,
          pageSize,
        }
      }));
      return this.getFiltered();
    },

    setFilter(query: string, order: 'asc' | 'desc') {
      patchState(store, (state) => ({
        filter: {
          ...state.filter,
          query,
          order,
        }
      }));
      return this.getFiltered();
    },

    async getFiltered() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const name = store.name() || '';
      const withPagination = await firstValueFrom(service.getFiltered(name, store.filter(), store.pagination()));

      patchState(store, () => ({
        isLoading: false,
        items: withPagination.data.map((item: CategoryApi) => {
          return {
            ...item,
            updatedAt: item.updated_at
          }
        }),
        total: withPagination.pagination.total
      }));
    },


    async getAll() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const items = await firstValueFrom(service.getList());
      patchState(store, () => ({
        isLoading: false,
        fullList: items
      }));

    },
    clearCriteria() {
      patchState(store, () => ({
        name: '',
        filter: { query: '', order: 'asc' } as GetFilter,
        pagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE }
      }));
    },
    async create(data: CategoryCreate) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.create(data));

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during creation', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }

    },
    async update(id: number, data: CategoryCreate) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.update(id, data));

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during update', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    },
    async delete(ids: number[]) {
      patchState(store, () => ({
        isLoading: true,
      }));

      try {
        await firstValueFrom(service.delete(ids));

        return this.getFiltered();

      } catch (e) {
        snackBar.open('Error during removing', '', { duration: 2000, horizontalPosition: 'end', panelClass: 'error-toast' });
        console.error(e);
      } finally {
        patchState(store, () => ({
          isLoading: false
        }));
      }
    }
  }))
);
